import { useState, useEffect, useCallback } from 'react';
import { Button, Select, TextInput, Card } from '@etherisc/ui-kit';
import { getEnabledPaymentMethods } from '../../config/countries/index';
import { PaymentMethodConfig, PaymentField } from '../../config/countries/types';
import { FarmerPaymentMethod } from '../../types/farmer';
import { PlusIcon, TrashIcon } from 'lucide-react';

interface PaymentMethodsProps {
  countryCode: string;
  paymentMethods: FarmerPaymentMethod[];
  onChange: (methods: FarmerPaymentMethod[]) => void;
  disabled?: boolean;
  errors?: Record<string, string>;
  farmerData?: {
    name?: string;
    phone?: string;
  };
}

export function PaymentMethods({
  countryCode,
  paymentMethods,
  onChange,
  disabled = false,
  errors = {},
  farmerData
}: PaymentMethodsProps) {
  const [availableMethods, setAvailableMethods] = useState<PaymentMethodConfig[]>([]);

  // Load available payment methods when country changes
  useEffect(() => {
    const methods = getEnabledPaymentMethods(countryCode);
    setAvailableMethods(methods);
    
    // Ensure paymentMethods is always an array
    const currentMethods = Array.isArray(paymentMethods) ? paymentMethods : [];
    
    // Filter out invalid payment methods for the new country
    const validMethods = currentMethods.filter(method =>
      methods.some(config => config.type === method.type)
    );
    
    if (validMethods.length !== currentMethods.length) {
      onChange(validMethods);
    }
  }, [countryCode, paymentMethods, onChange]);

  const addPaymentMethod = useCallback((type: string) => {
    const methodConfig = availableMethods.find(m => m.type === type);
    if (!methodConfig) return;

    // Initialize with empty values for all required fields
    const newMethod: FarmerPaymentMethod = { type } as FarmerPaymentMethod;
    methodConfig.fields.forEach(field => {
      if (field.required) {
        // Auto-populate fields based on farmer data
        let defaultValue = '';
        
        if (farmerData) {
          // For mobile money, pre-populate phone and name
          if (type === 'mobile_money') {
            if (field.name === 'registered_mobile_number' && farmerData.phone) {
              defaultValue = farmerData.phone;
            } else if (field.name === 'full_name' && farmerData.name) {
              defaultValue = farmerData.name;
            }
          }
          // For bank transfer, pre-populate account name
          else if (type === 'bank_transfer') {
            if (field.name === 'account_name' && farmerData.name) {
              defaultValue = farmerData.name;
            }
          }
        }
        
        (newMethod as Record<string, unknown>)[field.name] = defaultValue;
      }
    });

    const currentMethods = Array.isArray(paymentMethods) ? paymentMethods : [];
    onChange([...currentMethods, newMethod]);
  }, [availableMethods, paymentMethods, onChange, farmerData]);

  const removePaymentMethod = useCallback((index: number) => {
    const currentMethods = Array.isArray(paymentMethods) ? paymentMethods : [];
    const newMethods = currentMethods.filter((_, i) => i !== index);
    onChange(newMethods);
  }, [paymentMethods, onChange]);

  const updatePaymentMethod = useCallback((index: number, field: string, value: string) => {
    const currentMethods = Array.isArray(paymentMethods) ? paymentMethods : [];
    const newMethods = [...currentMethods];
    if (newMethods[index]) {
      (newMethods[index] as Record<string, unknown>)[field] = value;
      onChange(newMethods);
    }
  }, [paymentMethods, onChange]);

  const renderPaymentMethodForm = (method: FarmerPaymentMethod, index: number) => {
    const methodConfig = availableMethods.find(m => m.type === method.type);
    if (!methodConfig) return null;

    return (
      <Card key={index} className="p-4 border border-gray-200">
        <div className="flex justify-between items-start mb-4">
          <h4 className="text-md font-semibold text-gray-900">{methodConfig.name}</h4>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={() => removePaymentMethod(index)}
            disabled={disabled}
            className="text-red-600 hover:text-red-700"
          >
            <TrashIcon className="w-4 h-4" />
          </Button>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {methodConfig.fields.map(field => 
            renderField(field, method, index)
          )}
        </div>
      </Card>
    );
  };

  const renderField = (field: PaymentField, method: FarmerPaymentMethod, methodIndex: number) => {
    const fieldKey = `payment_${methodIndex}_${field.name}`;
    const value = (method as Record<string, unknown>)[field.name] as string || '';
    const error = errors[fieldKey];

    if (field.type === 'select') {
      const options = (field.options || []).map(option => ({
        value: option,
        label: option,
      }));

      return (
        <Select
          key={field.name}
          label={field.label}
          value={value}
          onValueChange={(val) => updatePaymentMethod(methodIndex, field.name, val)}
          options={options}
          disabled={disabled}
          error={error}
          placeholder={field.placeholder}
        />
      );
    }

    return (
      <TextInput
        key={field.name}
        label={field.label}
        value={value}
        onChange={(e) => updatePaymentMethod(methodIndex, field.name, e.target.value)}
        type={field.type}
        disabled={disabled}
        error={error}
        placeholder={field.placeholder}
      />
    );
  };

  if (availableMethods.length === 0) {
    return null;
  }

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <h3 className="text-lg font-semibold text-gray-900">Payment Details</h3>
        <div className="space-x-2">
          {availableMethods.map(method => (
            <Button
              key={method.type}
              type="button"
              variant="outline"
              size="sm"
              onClick={() => addPaymentMethod(method.type)}
              disabled={disabled}
              className="text-blue-600 border-blue-600 hover:bg-blue-50"
            >
              <PlusIcon className="w-4 h-4 mr-1" />
              Add {method.name}
            </Button>
          ))}
        </div>
      </div>

      {(!Array.isArray(paymentMethods) || paymentMethods.length === 0) ? (
        <div className="bg-amber-50 border border-amber-200 rounded p-3">
          <p className="text-amber-800 text-sm">No payment methods added yet.</p>
          <p className="text-sm text-amber-700">Payment details are optional during farmer creation, but are required for processing payouts.</p>
        </div>
      ) : (
        <div className="space-y-4">
          {paymentMethods.map((method, index) => 
            renderPaymentMethodForm(method, index)
          )}
        </div>
      )}
    </div>
  );
}